import {
  convertMeasure,
  isDefined,
  isMeasureWithValue,
  isRequired,
  mustBeEqual,
  mustBeGreaterOrEqual,
  mustBeLessOrEqual,
  mustContainInteger,
  mustNotBeEqual,
  NullableMeasure,
  Validation,
} from '@e2m-customer-portal/shared';
import { useTranslation } from 'react-i18next';
import { DefaultForecast, DefaultForecastInput } from '../../../generated/graphql';

interface DefaultTableRules {
  validateMaximum: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];

  validateMinimum: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];

  validateAuxiliary: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];

  validateProduction: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];

  warnMaximum: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];

  warnMinimum: (row: DefaultForecast | DefaultForecastInput) => Validation<string>[];
}

export function useDefaultTableRules(
  installedPower: NullableMeasure,
  powerLevelsRules: Validation<string | number> | undefined,
  unit: string,
): DefaultTableRules {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');

  const commonRules = [
    isRequired(tv('COMMON_REQUIRED')),
    mustContainInteger(tv('COMMON_MUST_CONTAIN_POSITIVE_INTEGER')),
    mustBeGreaterOrEqual(0, tv('COMMON_MUST_CONTAIN_POSITIVE_INTEGER')),
  ];

  return {
    validateMaximum(row) {
      return [
        ...commonRules,
        powerLevelsRules, //PLL_01 PLL_02
        mustBeLessOrEqual(
          convertMeasure(installedPower, unit).value ?? 0,
          t('ANNUAL_PLANNING_VALIDATION_ERROR_MAX_PINST', {
            value: installedPower,
            unit,
          }),
        ),
        isMeasureWithValue(row.minimum)
          ? mustBeGreaterOrEqual(convertMeasure(row.minimum, unit).value, t('ANNUAL_PLANNING_VALIDATION_ERROR_MAX_MIN'))
          : null,
      ].filter(isDefined);
    },

    validateMinimum(row) {
      return [
        ...commonRules,
        powerLevelsRules, //PLL_03 PLL_04
        isMeasureWithValue(row.maximum)
          ? mustBeLessOrEqual(convertMeasure(row.maximum, unit).value, t('ANNUAL_PLANNING_VALIDATION_ERROR_MIN_MAX'))
          : null,
      ].filter(isDefined);
    },

    validateAuxiliary() {
      return [
        ...commonRules,
        mustBeLessOrEqual(
          convertMeasure(installedPower, unit).value ?? 0,
          t('ANNUAL_PLANNING_VALIDATION_ERROR_AUXILIARY_CONSUMPTION_PINST', {
            value: installedPower,
            unit,
          }),
        ),
      ].filter(isDefined);
    },

    validateProduction() {
      return [
        ...commonRules,
        powerLevelsRules, //PLL_05 PLL_06
        mustBeLessOrEqual(
          convertMeasure(installedPower, unit).value ?? 0,
          t('ANNUAL_PLANNING_VALIDATION_ERROR_PROD_PINST', {
            value: installedPower,
            unit,
          }),
        ),
      ].filter(isDefined);
    },

    warnMaximum(row) {
      return [
        convertMeasure(row.minimum, unit).value === 0
          ? mustNotBeEqual(0, t('ANNUAL_PLANNING_VALIDATION_WARNING_MAX_AND_MIN_EQUAL_ZERO'))
          : null,
        mustBeEqual(
          convertMeasure(installedPower, unit).value ?? 0,
          t('ANNUAL_PLANNING_VALIDATION_WARNING_MAX_BELOW_PINST', {
            value: installedPower,
            unit,
          }),
        ),
        isMeasureWithValue(row.minimum)
          ? mustNotBeEqual(
              convertMeasure(row.minimum, unit).value,
              t('ANNUAL_PLANNING_VALIDATION_WARNING_MAX_EQUAL_MIN'),
            )
          : null,
      ].filter(isDefined);
    },

    warnMinimum(row) {
      return [
        convertMeasure(row.maximum, unit).value === 0
          ? mustNotBeEqual(0, t('ANNUAL_PLANNING_VALIDATION_WARNING_MAX_AND_MIN_EQUAL_ZERO'))
          : null,
        isMeasureWithValue(row.maximum)
          ? mustNotBeEqual(
              convertMeasure(row.maximum, unit).value,
              t('ANNUAL_PLANNING_VALIDATION_WARNING_MAX_EQUAL_MIN'),
            )
          : null,
      ].filter(isDefined);
    },
  };
}
